<template>
  <div :class="$style.base">
    <div :class="$style.heading" v-if="timeSlots.length > 0">
      {{ $t('date.choose_time', { date: formattedDate }) }}
    </div>
    <div :class="$style.list" v-if="timeSlots.length > 0">
      <TimePickerItem
        v-for="(timeslot, index) in timeSlots"
        :key="index"
        :timeslot="timeslot"
        :isSelected="timeslot.time === time"
        @click="selectTimeslot(timeslot)"
        v-test="'time-item'"
      />
    </div>
    <TimePickerFirstAvailability v-else />
    <div :class="$style.legend" v-if="totalPrice">
      <div
        :class="$style.legendItem"
        v-if="availableSlotsWithPriceDecrease"
        v-test="'legend-decrease'"
      >
        <span :class="`${$style.legendItemIcon} ${$style.discount}`" />{{
          $t('availabilities.discount')
        }}
      </div>
      <div
        :class="$style.legendItem"
        v-if="availableSlotsWithPriceIncrease"
        v-test="'legend-increase'"
      >
        <span :class="$style.legendItemIcon" />{{
          $t('availabilities.higher_prices')
        }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TimeSlot } from '@/types';

import { storeToRefs } from 'pinia';
import { computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAvailabilityStore } from '@/stores/availability';
import { useBookingStore } from '@/stores/booking';
import widgetSettings from '@/widget-settings';

import TimePickerFirstAvailability from './TimePickerFirstAvailability.vue';
import TimePickerItem from './TimePickerItem.vue';

const emit = defineEmits(['submit']);
const gtag = inject<any>('gtag');

const { d } = useI18n();

const { date, time, employeeId, totalPrice, timeSlotPriceAdjustment } =
  storeToRefs(useBookingStore());
const { availabilities } = storeToRefs(useAvailabilityStore());

const formattedDate = computed(() =>
  d(new Date(`${date.value}T00:00:00`), 'full')
);

const timeSlots = computed(() => {
  if (employeeId.value === null) {
    return (
      availabilities.value.find(
        (availability) => availability.date === date.value
      )?.timeslots || []
    );
  }

  return (
    availabilities.value
      .find((availability) => availability.date === date.value)
      ?.timeslots.filter((timeslot) =>
        timeslot.resource_ids.includes(employeeId.value as number)
      ) || []
  );
});
const selectTimeslot = (timeslot: TimeSlot) => {
  time.value = timeslot.time;

  if (timeslot.price_adjustment) {
    timeSlotPriceAdjustment.value =
      timeslot.price_adjustment.adjustment_type === 'percentage_decrease'
        ? -timeslot.price_adjustment.amount
        : timeslot.price_adjustment.amount;
  } else {
    timeSlotPriceAdjustment.value = 0;
  }

  if (!widgetSettings.bookingId) {
    emit('submit');
  }

  gtag.trackEvent('time_select', {
    time: timeslot.time
  });
};

const availableSlotsWithPriceDecrease = computed(() =>
  timeSlots.value?.some(
    (timeslot) =>
      timeslot.price_adjustment?.adjustment_type === 'percentage_decrease'
  )
);
const availableSlotsWithPriceIncrease = computed(() =>
  timeSlots.value?.some(
    (timeslot) =>
      timeslot.price_adjustment?.adjustment_type === 'percentage_increase'
  )
);
</script>

<style lang="scss" module>
.base {
  padding: $spacing;
}
.heading {
  color: #9c9aa0;
  margin-bottom: $spacing * 1.5;
  text-align: center;
}
.list {
  display: flex;
  flex-wrap: wrap;
}

.showMore {
  margin-top: $spacing;
  text-align: center;
}

.showMoreLink {
  @include link;
}

.legend {
  margin-top: $spacing;
  display: flex;
  flex-direction: row;
}

.legendItem {
  margin-right: $spacing;
}

.legendItemIcon {
  height: 13px;
  width: 13px;
  border-radius: 50%;
  background-color: #fd832d;
  display: inline-block;
  margin-right: 0.5 * $spacing;
}
.discount {
  background-color: #2ac77c;
}
</style>
